import { useEffect, useState } from "react";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useTranslation } from "react-i18next";
import { useKit, useUpdateKit, type BrandKit } from "@/hooks/use-kit";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ColorSwatchEditor } from "@/components/settings/ColorSwatchEditor";
import { FontFamilySlot } from "@/components/settings/FontFamilySlot";
import { LogoUploader } from "@/components/settings/LogoUploader";
import { BrandFontFaceInjector } from "@/components/settings/BrandFontFaceInjector";
import { useFontFamilies } from "@/hooks/use-font-families";
import { toast } from "sonner";
import { CONTENT_LANGUAGE_NAMES } from "@content-os/config";

const DEFAULTS = {
  colors: { primary: "#0f172a", secondary: "#475569", accent: "#3b82f6", background: "#ffffff", text: "#0f172a" },
  fonts: { heading: "Inter", body: "Inter" },
} as const;

const VOICE_MAX = 500;

export function BrandSettingsKitPage() {
  const { t } = useTranslation("settings");
  const { brand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const canEditKit = canManageBrand;
  const brandId = brand?.id ?? "";
  const { data, isLoading } = useKit(brandId);
  const updateKit = useUpdateKit(brandId);
  const families = useFontFamilies(brandId);

  const [draft, setDraft] = useState<BrandKit | null>(null);

  useEffect(() => {
    if (data) {
      setDraft({
        colors: { ...DEFAULTS.colors, ...(data.colors ?? {}) },
        fonts: { ...DEFAULTS.fonts, ...(data.fonts ?? {}) },
        logoUrl: data.logoUrl ?? null,
        voiceProfile: data.voiceProfile ?? null,
        primaryLogoAssetId: data.primaryLogoAssetId ?? null,
        headingFontFamilyId: data.headingFontFamilyId ?? null,
        bodyFontFamilyId: data.bodyFontFamilyId ?? null,
        primaryLogo: data.primaryLogo ?? null,
        headingFontFamily: data.headingFontFamily ?? null,
        bodyFontFamily: data.bodyFontFamily ?? null,
        contentLanguage: data.contentLanguage ?? "en",
        languageVariant: data.languageVariant ?? null,
      });
    }
  }, [data]);

  if (isLoading || !draft) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // The Save button only controls colors + voice profile. Fonts and logo
  // are persisted asynchronously by their own uploaders/pickers, so they
  // are intentionally excluded from the dirty check (and the save payload)
  // to avoid clobbering newer values.
  const savedSnapshot = {
    colors: { ...DEFAULTS.colors, ...(data?.colors ?? {}) },
    voiceProfile: data?.voiceProfile ?? null,
    contentLanguage: data?.contentLanguage ?? "en",
    languageVariant: data?.languageVariant ?? null,
  };
  const dirty =
    JSON.stringify({ colors: draft.colors, voiceProfile: draft.voiceProfile, contentLanguage: draft.contentLanguage, languageVariant: draft.languageVariant }) !==
    JSON.stringify(savedSnapshot);

  async function save() {
    if (!draft) return;
    await updateKit.mutateAsync({
      colors: draft.colors,
      voiceProfile: draft.voiceProfile,
      contentLanguage: draft.contentLanguage,
      languageVariant: draft.languageVariant,
    });
    toast.success(t("kit.saved"));
  }

  const colorKeys = ["primary", "secondary", "accent", "background", "text"] as const;

  return (
    <div className="px-6 py-8 w-full space-y-6">
      <BrandFontFaceInjector families={[data?.headingFontFamily ?? null, data?.bodyFontFamily ?? null]} />
      <header>
        <h1 className="text-2xl font-semibold">{t("kit.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("kit.subtitle")}</p>
      </header>

      <section className="rounded-lg border p-4 space-y-4">
        <h2 className="text-lg font-semibold">{t("kit.colors")}</h2>
        <div className="flex flex-wrap gap-4">
          {colorKeys.map((key) => (
            <ColorSwatchEditor
              key={key}
              label={key[0].toUpperCase() + key.slice(1)}
              value={draft.colors[key] ?? DEFAULTS.colors[key]}
              defaultValue={DEFAULTS.colors[key]}
              onChange={(hex) => canEditKit && setDraft({ ...draft, colors: { ...draft.colors, [key]: hex } })}
            />
          ))}
        </div>
      </section>

      <section className="rounded-lg border p-4 space-y-4">
        <h2 className="text-lg font-semibold">{t("kit.typography")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FontFamilySlot
            label={t("kit.headingSlot")}
            brandId={brandId}
            current={data?.headingFontFamily ?? null}
            disabled={!canEditKit}
            onChange={(id) => {
              const fam = families.data?.find((f) => f.id === id);
              updateKit.mutate({
                headingFontFamilyId: id,
                fonts: { ...(draft.fonts ?? {}), heading: fam?.name ?? draft.fonts?.heading },
              });
            }}
          />
          <FontFamilySlot
            label={t("kit.bodySlot")}
            brandId={brandId}
            current={data?.bodyFontFamily ?? null}
            disabled={!canEditKit}
            onChange={(id) => {
              const fam = families.data?.find((f) => f.id === id);
              updateKit.mutate({
                bodyFontFamilyId: id,
                fonts: { ...(draft.fonts ?? {}), body: fam?.name ?? draft.fonts?.body },
              });
            }}
          />
        </div>
      </section>

      <section className="space-y-4">
        <LogoUploader
          brandId={brandId}
          currentUrl={draft.primaryLogo?.url ?? draft.logoUrl ?? null}
          disabled={!canEditKit}
          onChange={(assetId, url) => {
            setDraft({
              ...draft,
              primaryLogoAssetId: assetId,
              logoUrl: url,
              primaryLogo: assetId && url
                ? {
                    id: assetId,
                    url,
                    mimeType: draft.primaryLogo?.mimeType ?? "",
                    width: draft.primaryLogo?.width ?? null,
                    height: draft.primaryLogo?.height ?? null,
                  }
                : null,
            });
            updateKit.mutate({ primaryLogoAssetId: assetId, logoUrl: url });
          }}
        />
      </section>

      <section className="rounded-lg border p-4 space-y-2">
        <h2 className="text-lg font-semibold">{t("kit.voiceProfile")}</h2>
        <textarea
          value={draft.voiceProfile ?? ""}
          maxLength={VOICE_MAX}
          onChange={(e) => canEditKit && setDraft({ ...draft, voiceProfile: e.target.value || null })}
          rows={6}
          placeholder={t("kit.voicePlaceholder")}
          className="block w-full rounded border px-3 py-2 text-sm"
        />
        <div className="text-xs text-muted-foreground text-right">
          {(draft.voiceProfile ?? "").length} / {VOICE_MAX}
        </div>
      </section>

      <section className="rounded-lg border p-4 space-y-2">
        <h2 className="text-lg font-semibold">{t("kit.contentLanguage")}</h2>
        <label className="block text-sm font-medium">{t("kit.contentLanguageLabel")}</label>
        <select
          value={draft.contentLanguage ?? "en"}
          disabled={!canEditKit}
          onChange={(e) => canEditKit && setDraft({ ...draft, contentLanguage: e.target.value })}
          className="block w-full rounded border px-3 py-2 text-sm"
        >
          {Object.entries(CONTENT_LANGUAGE_NAMES).map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
        <label className="block text-sm font-medium">{t("kit.dialectLabel")}</label>
        <textarea
          value={draft.languageVariant ?? ""}
          disabled={!canEditKit}
          placeholder={t("kit.dialectPlaceholder")}
          onChange={(e) => canEditKit && setDraft({ ...draft, languageVariant: e.target.value || null })}
          rows={3}
          className="block w-full rounded border px-3 py-2 text-sm"
        />
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={!dirty || updateKit.isPending || !canEditKit}>
          {updateKit.isPending ? t("actions.saving", { ns: "common" }) : t("actions.save", { ns: "common" })}
        </Button>
      </div>
    </div>
  );
}
